import { codeUnitString } from "./code-unit-string.js";
import { singleLineText } from "./single-line-text.js";
import { z } from "zod";
//#region src/dp-logs-query.ts
/**
* The DP log query: `POST /v1/admin/data-planes/:id/logs/query` (site-admin-api
* `ObservabilityController.queryLogs`, gate `cp:observability:read-logs`).
*
* Plan `2026-10-21-operator-tui`, defaults rows F1 and F3, decision D10 (the route moves to
* `@AuditAction('dp_logs.query')`), rulings R-005 and R-007.
*
* Producers: the route binds `dpLogsQueryRequestSchema` itself, with the checks that
* `LogsQueryService.buildLogQL` also runs (a DNS-1123 label for `namespace` and each pod, a short
* level word). So a client and the route refuse the same body, and every accepted value fits a
* read-audit row (`readAuditDetailsSchema`). The response is `LogsQueryService.query`, the type
* `DpLogsQueryResponse`, whose entries come from `LokiProxyClientImpl`.
*/
/** The most pod names one DP log query names. */
const DP_LOGS_MAX_PODS = 100;
/** The most level names one DP log query names. */
const DP_LOGS_MAX_LEVELS = 20;
/** The longest level name, in UTF-16 code units. The level allowlist holds words of at most 7. */
const DP_LOGS_LEVEL_MAX_LENGTH = 16;
/**
* The longest `start` or `end` text of a DP log query. A 19-digit nanosecond epoch is shorter. An
* ISO-8601 instant with a long fraction can be longer: this query refuses it, and the app and fleet
* log queries accept it, so `fitReadAuditDetails` cuts it in their rows.
*/
const DP_LOGS_INSTANT_MAX_LENGTH = 64;
/** Tell whether a text is an instant Loki takes: an integer epoch, or a date `Date.parse` reads. */
function isDpLogsInstant(value) {
	if (/^\d+$/.test(value)) return true;
	return !Number.isNaN(Date.parse(value));
}
/** A window bound: at most 64 characters, an integer epoch or a date `Date.parse` reads. */
const dpLogsInstantSchema = codeUnitString().max(64).refine(isDpLogsInstant, { message: "must be an ISO-8601 instant or an integer epoch" });
/**
* The namespace filter: a DNS-1123 label (lower case, digits and hyphens, at most 63), or the
* empty text, which the service reads as no filter.
*/
const dpLogsNamespaceSchema = codeUnitString().max(63).regex(/^(?:[a-z0-9](?:[-a-z0-9]{0,61}[a-z0-9])?)?$/, "must be a DNS-1123 label or empty");
/** One pod filter: a DNS-1123 label, 1 to 63 characters, as the Loki pod regex needs. */
const dpLogsPodNameSchema = codeUnitString().min(1).max(63).regex(/^[a-z0-9](?:[-a-z0-9]{0,61}[a-z0-9])?$/, "must be a DNS-1123 label");
/**
* One level filter: 1 to 16 characters on one line. The service then checks the word against its
* level allowlist, any case, and answers 400 for another word.
*/
const dpLogsLevelSchema = singleLineText(16);
/**
* The body of the DP log query. Every field is optional:
*
* - `namespace` ({@link dpLogsNamespaceSchema}), `pods` (at most 100 of
*   {@link dpLogsPodNameSchema}), `levels` (at most 20 of {@link dpLogsLevelSchema}), `search`: the
*   filters the CP turns into LogQL after its own allowlist and escape.
* - `start`, `end` ({@link dpLogsInstantSchema}): the window. The CP defaults to the last hour.
* - `limit`: 1 to 1000 lines, 100 when absent.
* - `cursor`: the `nextCursor` of the page before. It replaces `end`.
*
* Any other key is a 400 at the route and a parse failure here.
*/
const dpLogsQueryRequestSchema = z.object({
	namespace: dpLogsNamespaceSchema.optional(),
	pods: z.array(dpLogsPodNameSchema).max(100).optional(),
	levels: z.array(dpLogsLevelSchema).max(20).optional(),
	search: z.string().optional(),
	start: dpLogsInstantSchema.optional(),
	end: dpLogsInstantSchema.optional(),
	limit: z.number().int().min(1).max(1e3).optional(),
	cursor: z.string().optional()
}).strict();
/**
* One log line. `timestamp` is ISO-8601 at millisecond resolution. `level` is the stream's
* `level` or `detected_level` label, or `unknown`. `traceId` is absent when the line names none.
*/
const dpLogEntrySchema = z.object({
	timestamp: z.iso.datetime(),
	pod: z.string(),
	container: z.string(),
	level: z.string(),
	message: z.string(),
	traceId: z.string().min(1).optional()
}).strict();
/**
* The answer of the DP log query, newest first. `nextCursor` is `null` when the page held fewer
* lines than `limit`, so no older page exists in the window.
*/
const dpLogsQueryResponseSchema = z.object({
	entries: z.array(dpLogEntrySchema),
	nextCursor: z.string().min(1).nullable()
}).strict();
//#endregion
export { DP_LOGS_INSTANT_MAX_LENGTH, DP_LOGS_LEVEL_MAX_LENGTH, DP_LOGS_MAX_LEVELS, DP_LOGS_MAX_PODS, dpLogEntrySchema, dpLogsInstantSchema, dpLogsLevelSchema, dpLogsNamespaceSchema, dpLogsPodNameSchema, dpLogsQueryRequestSchema, dpLogsQueryResponseSchema, isDpLogsInstant };

//# sourceMappingURL=dp-logs-query.js.map